import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signIn, getCurrentSession } from '../../services/authService';
import { isValidEmail } from '../../utils/validation';
import { setPageTitle } from '../../utils/pageTitle';
import './Auth.css';

/**
 * Login component for user authentication
 * Allows users to sign in with email and password
 */
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Field validation state
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    password: ''
  });
  const navigate = useNavigate();

  // Check if user is already logged in and set page title
  useEffect(() => {
    // Set the page title for the login page
    setPageTitle('Login');
    
    const checkSession = async () => {
      const { session } = await getCurrentSession();
      if (session) {
        navigate('/');
      }
    };

    checkSession();
    
    // Clean up function to reset title when component unmounts
    return () => {
      // No need to reset as other pages will set their own titles
    };
  }, [navigate]);

  // Validate email as user types
  const validateEmail = (email) => {
    if (!email) {
      return 'Email is required';
    }
    if (!isValidEmail(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  // Validate password as user types
  const validatePassword = (password) => {
    if (!password) {
      return 'Password is required';
    }
    return '';
  };

  // Handle input changes with validation
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setValidationErrors(prev => ({
      ...prev,
      email: validateEmail(value)
    }));
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setValidationErrors(prev => ({
      ...prev,
      password: validatePassword(value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields before submission
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    
    const newValidationErrors = {
      email: emailError,
      password: passwordError
    };
    
    setValidationErrors(newValidationErrors);
    
    // Check if there are any validation errors
    if (emailError || passwordError) {
      setError('Please fix the validation errors before submitting');
      return;
    }
    
    setLoading(true);
    setError(null);

    try {

      // Attempt to sign in
      const { error } = await signIn(email, password);
      
      if (error) throw error;
      
      // Redirect to dashboard on successful login
      navigate('/');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Welcome to Unissn</h2>
          <p>Sign in to your account</p>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email" className="form-label">Email</label>
            <input
              type="email"
              id="email"
              className={`form-input ${validationErrors.email ? 'input-error' : ''}`}
              value={email}
              onChange={handleEmailChange}
              onBlur={() => setValidationErrors(prev => ({ ...prev, email: validateEmail(email) }))}
              placeholder="Enter your email"
              required
            />
            {validationErrors.email && <div className="error-message">{validationErrors.email}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">Password</label>
            <input
              type="password"
              id="password"
              className={`form-input ${validationErrors.password ? 'input-error' : ''}`}
              value={password}
              onChange={handlePasswordChange}
              onBlur={() => setValidationErrors(prev => ({ ...prev, password: validatePassword(password) }))}
              placeholder="Enter your password"
              required
            />
            {validationErrors.password && <div className="error-message">{validationErrors.password}</div>}
          </div>

          <div className="form-group auth-links">
            <Link to="/reset-password" className="forgot-password">
              Forgot password?
            </Link>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary auth-btn"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Don't have an account?{' '}
            <Link to="/register" className="auth-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
